import { useCallback } from 'react'

import { type SlackUser } from '@webapp/platform/integrations/entities/SlackUser'
import { useLinkSlackUserPresenter } from '@webapp/platform/integrations/features/link-slack-user'
import { useListSlackUsersPresenter } from '@webapp/platform/integrations/features/list-slack-users'
import { useSyncUnlinkedSlackUsersPresenter } from '@webapp/platform/integrations/features/sync-unlinked-slack-users'
import { useUnlinkSlackUserPresenter } from '@webapp/platform/integrations/features/unlink-slack-user'

export const useSlackUserLinkingSection = () => {
  const { linkUser, isLinking } = useLinkSlackUserPresenter()
  const { unlinkUser, isUnlinking } = useUnlinkSlackUserPresenter()
  const { syncUnlinkedUsers, isSyncing } = useSyncUnlinkedSlackUsersPresenter()
  const { slackUserCache } = useListSlackUsersPresenter()

  const handleUserLinkChange = useCallback(
    async (userId: number, slackUser: SlackUser | null): Promise<void> => {
      if (slackUser === null) {
        await unlinkUser(userId)
      } else {
        try {
          await linkUser(userId, slackUser.id)
        } catch {
          // Error already handled by presenter (toast shown, error re-thrown).
        }
      }
    },
    [linkUser, unlinkUser]
  )

  const handleSyncUnlinkedUsers = useCallback(async () => {
    try {
      await syncUnlinkedUsers()
    } catch {
      // Error already handled by presenter (toast shown).
    }
  }, [syncUnlinkedUsers])

  return {
    data: {
      slackUserCache,
    },
    methods: {
      handleUserLinkChange,
      syncUnlinkedUsers: handleSyncUnlinkedUsers,
    },
    states: {
      isSyncing,
      isLinking,
      isUnlinking,
    },
  }
}
